import React from "react";
import { Box, Typography } from "@mui/material";
import ReactMarkdown from "react-markdown";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";

interface GENAITileBlockProps {
  title: string | number;
  subtitle?: string | number;
  description: string | number;
  icon?: string;
  color?: string;
}

const lightBg = (hex: string): string => {
  const num = parseInt(hex.replace("#", ""), 16);
  const R = (num >> 16) & 255;
  const G = (num >> 8) & 255;
  const B = num & 255;
  const lighter = Math.round(R * 0.299 + G * 0.587 + B * 0.114) > 186 ? 240 : 245;
  return `rgba(${R}, ${G}, ${B}, 0.08)`;
};

const GENAITileBlock: React.FC<GENAITileBlockProps> = ({
  title,
  subtitle,
  description,
  icon,
  color = "#60a5fa",
}) => {
  const normalizedTitle = React.useMemo(() => String(title ?? ""), [title]);
  const normalizedSubtitle = React.useMemo(
    () => (subtitle !== undefined && subtitle !== null ? String(subtitle) : ""),
    [subtitle]
  );
  const normalizedDescription = React.useMemo(
    () => String(description ?? ""),
    [description]
  );

  const lightBgColor = React.useMemo(() => lightBg(color), [color]);

  const IconComponent = icon ? (LucideIcons as any)[
    icon
      .split("-")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("")
  ] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      style={{ height: "100%", display: "flex", width: "100%" }}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          border: `1.5px solid ${color}`,
          backgroundColor: lightBgColor,
          overflow: "hidden",
          transition: "all 0.2s ease",
          fontFamily: "'Inter', sans-serif",
          "&:hover": {
            boxShadow: `0 6px 16px ${color}33`,
            transform: "translateY(-2px)",
          },
        }}
      >
        <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
          {/* Icon and Title Row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, justifyContent: "center" }}>
            {IconComponent && (
              <Box sx={{ flexShrink: 0 }}>
                <IconComponent size={24} color={color} strokeWidth={2.5} />
              </Box>
            )}
            <Box sx={{ "& p": { margin: 0 } }}>
              <Typography
                component="div"
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: color,
                  lineHeight: 1.4,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                <ReactMarkdown>{normalizedTitle}</ReactMarkdown>
              </Typography>
            </Box>
          </Box>

          {/* Subtitle */}
          {normalizedSubtitle && (
            <Box sx={{ mb: 1, "& p": { margin: 0 } }}>
              <Typography
                component="div"
                sx={{
                  fontSize: "0.8rem",
                  color: "#000000",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                <ReactMarkdown>{normalizedSubtitle}</ReactMarkdown>
              </Typography>
            </Box>
          )}

          {/* Description */}
          <Box
            sx={{
              fontFamily: "'Inter', sans-serif",
              "& p": {
                margin: 0,
                fontSize: "0.9rem",
                lineHeight: 1.5,
                color: "#000000",
                fontWeight: 500,
              },
            }}
          >
            <Typography variant="body2" component="div">
              <ReactMarkdown>{normalizedDescription}</ReactMarkdown>
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

export default GENAITileBlock;
